import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChartManifest } from '../chart/style';
import { chartBaseUrl } from '../chart/chartSource';
import {
  type DownloadProgress,
  type Region,
  cacheUsage,
  clearChartCache,
  downloadRegion,
  estimateTileCount,
  isChartCacheAvailable,
  requestPersistentStorage,
  storageEstimate,
} from '../storage/chartCache';

export interface OfflinePanelProps {
  manifest: ChartManifest;
  /** The area currently on screen — what "download this area" means. */
  currentRegion: Region | null;
  onClose: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/**
 * Offline chart management.
 *
 * Downloading is explicit and its cost is stated up front. A region that looks
 * modest zoomed out can be tens of thousands of tiles at harbour scale, and
 * discovering that halfway through on marina wifi is exactly the wrong moment.
 */
export function OfflinePanel({
  manifest,
  currentRegion,
  onClose,
}: OfflinePanelProps): React.JSX.Element {
  const [usage, setUsage] = useState<{ entries: number; bytes: number } | null>(null);
  const [quota, setQuota] = useState<{ usage: number; quota: number } | null>(null);
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);

  const available = isChartCacheAvailable();

  const refresh = useCallback(async () => {
    if (!available) return;
    setUsage(await cacheUsage());
    setQuota(await storageEstimate());
  }, [available]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => () => abort.current?.abort(), []);

  const estimate = currentRegion ? estimateTileCount(manifest, currentRegion) : 0;

  const start = async (): Promise<void> => {
    if (!currentRegion) return;
    setBusy(true);
    setStatus(null);
    abort.current = new AbortController();

    // Ask before a download the skipper is going to rely on. Advisory only.
    await requestPersistentStorage();

    try {
      const result = await downloadRegion(
        manifest, chartBaseUrl(), currentRegion, setProgress, abort.current.signal,
      );
      setStatus(
        `Saved ${result.tiles.toLocaleString()} tiles (${formatBytes(result.bytes)}).`
        + (result.failed > 0
          ? ` ${result.failed} failed — run it again to fill the gaps.`
          : ' This area is now available offline.'),
      );
    } catch (error) {
      setStatus(
        error instanceof DOMException && error.name === 'AbortError'
          ? 'Download stopped. What was already saved is kept.'
          : 'Download failed. What was already saved is kept.',
      );
    } finally {
      setBusy(false);
      setProgress(null);
      abort.current = null;
      void refresh();
    }
  };

  return (
    <div className="sheet sheet--offline" role="dialog" aria-label="Offline charts">
      <div className="sheet__header">
        <h2>Offline charts</h2>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {!available ? (
        <p className="sheet__hint">
          This browser cannot store charts offline. The app will still work, but
          only where it has a signal.
        </p>
      ) : (
        <>
          <p className="sheet__hint">
            Download the area you are looking at so it works without a signal.
            Do this before you leave the dock.
          </p>

          <dl className="offline__stats">
            <div>
              <dt>Stored</dt>
              <dd>{usage ? formatBytes(usage.bytes) : '—'}</dd>
            </div>
            <div>
              <dt>Tiles</dt>
              <dd>{usage ? usage.entries.toLocaleString() : '—'}</dd>
            </div>
            <div>
              <dt>This area</dt>
              <dd>{currentRegion ? `${estimate.toLocaleString()} tiles` : '—'}</dd>
            </div>
          </dl>

          {quota && quota.quota > 0 && (
            <p className="sheet__hint sheet__hint--small">
              Using {formatBytes(quota.usage)} of about {formatBytes(quota.quota)} the
              browser allows.
            </p>
          )}

          {estimate > 40_000 && !busy && (
            <p className="route__warning">
              That is a large area — {estimate.toLocaleString()} tiles. Zoom in
              before downloading unless you have time and space to spare.
            </p>
          )}

          {progress && (
            <div className="offline__progress">
              <div
                className="offline__bar"
                style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
              />
              <span>
                {progress.done.toLocaleString()} / {progress.total.toLocaleString()} tiles ·{' '}
                {formatBytes(progress.bytes)}
              </span>
            </div>
          )}

          {status && <p className="offline__status">{status}</p>}

          <div className="route__actions">
            {busy ? (
              <button type="button" onClick={() => abort.current?.abort()}>Stop</button>
            ) : (
              <button type="button" onClick={() => void start()} disabled={!currentRegion}>
                Download this area
              </button>
            )}
            <button
              type="button"
              disabled={busy || !usage?.entries}
              onClick={async () => {
                await clearChartCache();
                setStatus('Offline charts removed.');
                void refresh();
              }}
            >
              Delete all
            </button>
          </div>
        </>
      )}
    </div>
  );
}
